import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Mail, 
  CheckCircle, 
  XCircle, 
  Loader,
  AlertCircle
} from 'lucide-react';
import api from './/../../api';

const MarketingUnsubscribe = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  // 상태 관리
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [subscriberInfo, setSubscriberInfo] = useState(null);

  // 토큰 검증 및 구독자 정보 조회
  useEffect(() => {
    if (!token) {
      setError('유효하지 않은 요청입니다.');
      setIsLoading(false);
      return;
    }

    const verifyToken = async () => {
      try {
        setIsLoading(true);
        const response = await api.get(`marketing/unsubscribe/?token=${token}`);
        setSubscriberInfo(response.data);
      } catch (err) {
        console.error('토큰 검증 오류:', err);
        if (err.response?.status === 404) {
          setError('유효하지 않은 토큰이거나 이미 처리된 요청입니다.');
        } else {
          setError('오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  // 구독 취소 처리
  const handleUnsubscribe = async () => {
    try {
      setIsProcessing(true);
      setError(null);
      
      await api.post('/marketing/unsubscribe/', { token });
      setSuccess(true);
    } catch (err) {
      console.error('구독 취소 오류:', err);
      if (err.response?.status === 404) {
        setError('유효하지 않은 토큰이거나 이미 처리된 요청입니다.');
      } else {
        setError('구독 취소 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // 홈으로 이동
  const goHome = () => {
    navigate('/');
  };

  // 로딩 상태
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">정보를 확인하고 있습니다...</p>
        </div>
      </div>
    );
  }

  // 에러 상태 (초기 에러)
  if (error && !subscriberInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            오류가 발생했습니다
          </h1>
          <p className="text-gray-600 mb-8">{error}</p>
          <button
            onClick={goHome}
            className="w-full px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors duration-300 font-medium"
          >
            홈으로 이동
          </button>
        </div>
      </div>
    );
  }

  // 성공 상태
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            구독이 취소되었습니다
          </h1>
          <p className="text-gray-600 mb-8">
            그동안 SWITCHUP을 이용해주셔서 감사합니다.<br />
            더 나은 서비스로 다시 찾아뵙겠습니다.
          </p>
          <button
            onClick={goHome}
            className="w-full px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors duration-300 font-medium"
          >
            홈으로 이동
          </button>
        </div>
      </div>
    );
  }

  // 구독 취소 확인 화면
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* 로고/헤더 */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">SWITCHUP</h1>
        </div>

        {/* 메인 카드 */}
        <div className="bg-white rounded-xl shadow-sm p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              구독 취소 확인
            </h2>
            <p className="text-gray-600">
              {subscriberInfo?.name}님, 정말로 이메일 구독을 취소하시겠습니까?
            </p>
            <p className="text-sm text-gray-500 mt-2">
              구독 이메일: {subscriberInfo?.email}
            </p>
          </div>

          {/* 안내 메시지 */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="ml-3">
                <p className="text-sm text-amber-800">
                  구독을 취소하시면 더 이상 유용한 팁과 인사이트를 받아보실 수 없습니다.
                </p>
              </div>
            </div>
          </div>

          {/* 에러 메시지 */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* 버튼 그룹 */}
          <div className="space-y-3">
            <button
              onClick={handleUnsubscribe}
              disabled={isProcessing}
              className={`w-full px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                isProcessing
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              {isProcessing ? (
                <span className="flex items-center justify-center">
                  <Loader className="w-4 h-4 animate-spin mr-2" />
                  처리 중...
                </span>
              ) : (
                '구독 취소하기'
              )}
            </button>
            
            <button
              onClick={goHome}
              disabled={isProcessing}
              className="w-full px-6 py-3 bg-white border border-gray-300 text-gray-800 rounded-lg hover:bg-gray-50 transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              취소하고 돌아가기
            </button>
          </div>
        </div>

        {/* 푸터 텍스트 */}
        <p className="text-center text-sm text-gray-500 mt-6">
          © 2025 SWITCHUP. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default MarketingUnsubscribe;